import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { Factory } from 'src/app/utils/Factory';
import { ICreatedInstanceId, IQueryCacheService } from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardMasterCampaignKpisDailySqlMap } from '../sql-maps/biviz-dashboard-master-campaign-kpis-daily-sql-map';
import { BiVizDashboardMasterPlayerKpisDailySqlMap } from '../sql-maps/biviz-dashboard-master-users-kpis-daily-sql-map';

export interface CampaignAttributionKpisMonthlyDataLoaderFactoryOptions
    extends ICreatedInstanceId,
        IQueryCacheService {
    gcpProjectId: GcpProjectId | (() => GcpProjectId);
}

export class CampaignAttributionKpisMonthlyDataLoaderFactory extends Factory<
    DataLoader,
    CampaignAttributionKpisMonthlyDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'CampaignAttributionKpisMonthlyDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */

    getInstance(
        options: CampaignAttributionKpisMonthlyDataLoaderFactoryOptions
    ) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getRetentionByFirstOpenDateDataLoaderFactory] Provided options are of type SalesDatetimeDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const campaignKPIs =
                    BiVizDashboardMasterCampaignKpisDailySqlMap;

                const campaignKPIsDatasourceColumns =
                    queryContext.datasourceColumns;
                // filter from context TRAFFIC_SOURCE
                // const campaignKPIsDatasourceColumns =
                //     queryContext.datasourceColumns.filter(
                //         (dsCol) => dsCol.id !== 'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC'
                //     );

                // identify columns not supported in datasource
                const campaignKPIsDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        campaignKPIsDatasourceColumns,
                        campaignKPIs.sqlMap.columns
                    );

                const campaignKPIsDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        campaignKPIsDsColumns.missing
                    );

                const campaignKPIsSQL = campaignKPIs.compileSQLStatements(
                    campaignKPIsDsColumns.present,
                    queryContext.settings,

                    { gcpProjectId: resolvedGcpProjectId },
                    {
                        where: [
                            'date >= DATE_SUB(DATE_TRUNC(CURRENT_DATE(), MONTH), INTERVAL 15 MONTH)',
                        ],
                    }
                );

                const companydashPlayerKPIsDaily =
                    BiVizDashboardMasterPlayerKpisDailySqlMap;

                const companydashPlayerKPIsDatasourceColumns =
                    queryContext.datasourceColumns;
                // filter from context FIRST_ATTRIBUTION_UNIFIED_TRAFFIC
                // const companydashUsersDaysDatasourceColumns =
                //     queryContext.datasourceColumns.filter(
                //         (dsCol) => dsCol.id !== 'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC'
                //     );

                // // add FIRST_ATTRIBUTION_UNIFIED_TRAFFIC to datasourceColumns
                // // this filter is not added to queryContext.datasourceColumns because it is not supported in datasource campaignKPIs
                // companydashUsersDaysDatasourceColumns.push({
                //     id: 'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC',
                //     state: {
                //         value: {
                //             options: ['Non-Organic'],
                //             filterType: 'include',
                //         },
                //         isFilterApplied: true,
                //         isSplitApplied: false,
                //     },
                // });

                // identify columns not supported in datasource
                const companydashPlayerKPIsDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        companydashPlayerKPIsDatasourceColumns,
                        companydashPlayerKPIsDaily.sqlMap.columns
                    );

                const companydashPlayerKPIsDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        companydashPlayerKPIsDsColumns.missing
                    );

                const companydashPlayerKPIsSQL =
                    companydashPlayerKPIsDaily.compileSQLStatements(
                        companydashPlayerKPIsDsColumns.present,
                        queryContext.settings,
                        { gcpProjectId: resolvedGcpProjectId },
                        {
                            where: [
                                'date >= DATE_SUB(DATE_TRUNC(CURRENT_DATE(), MONTH), INTERVAL 15 MONTH)',
                            ],
                        }
                    );

                const query = `
-- DataLoaderId: ${this.instanceId}
WITH campaignKPIs AS
(
  SELECT  
    ${campaignKPIsSQL.splitSelect}DATE_TRUNC(date, MONTH) AS date
    , SUM(cost_usd) AS cost_usd
    , SUM(cost_pln) AS cost_pln
  FROM \`${generateTableFullPath(
      resolvedGcpProjectId,
      campaignKPIs.sqlMap.dataset,
      campaignKPIs.sqlMap.tableId
  )}\`
  ${campaignKPIsSQL.where}
  GROUP BY ${campaignKPIsSQL.splitGroupBy}date
)

, usersKPIs AS
(
    SELECT 
        ${companydashPlayerKPIsSQL.splitSelect}DATE_TRUNC(date, MONTH) AS date
        , SUM(new_users) AS new_users
    FROM \`${generateTableFullPath(
        resolvedGcpProjectId,
        companydashPlayerKPIsDaily.sqlMap.dataset,
        companydashPlayerKPIsDaily.sqlMap.tableId
    )}\`
    ${companydashPlayerKPIsSQL.where}
    
    GROUP BY ${companydashPlayerKPIsSQL.splitGroupBy}date
)


SELECT
  ${campaignKPIsSQL.splitGroupBy}date
  , cost_usd
  , cost_pln
  , SAFE_DIVIDE(cost_usd, new_users) AS CPI_usd
  , SAFE_DIVIDE(cost_pln, new_users) AS CPI_pln
FROM campaignKPIs
LEFT JOIN usersKPIs USING (date${
                    campaignKPIsSQL.splitGroupBy !== '' ? ', split_group' : ''
                })
ORDER BY ${campaignKPIsSQL.splitGroupBy}date



`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: campaignKPIsDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        campaignKPIsDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
